/**
 * Catalog import wizard: upload → editable preview → commit.
 *
 * Functional spec: `qt_poc/catalog_qt.py` + `qt_poc/import_preview_dialog.py`.
 *
 * The preview is the whole point of the two-phase design. Dealer files are
 * messy: rows that are section headers rather than products, rows with no
 * manufacturer the importer could not resolve. The user fixes or deselects
 * those here, and **the commit persists the rows as edited** — it does not
 * re-read the uploaded file. That is what makes the corrections authoritative.
 *
 * Rows the importer flagged (`needs_attention`) are shown first and marked,
 * because a 4,000-row preview with the problems scattered through it is a
 * preview nobody reads.
 *
 * After a successful commit it offers to **remember** the manufacturers the
 * user resolved (`_maybe_save_aliases` on the desktop). Without that, the same
 * dealer file re-imported next month flags the same rows again and the same
 * corrections get retyped — the aliases are how an import gets quieter over
 * time instead of staying equally noisy forever.
 */
import { useMemo, useState } from 'react'
import {
  Alert, Badge, Button, Checkbox, FileInput, Group, Paper, Select,
  Stack, Text, TextInput, Tooltip,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import type { ColumnDef } from '@tanstack/react-table'

import { ApiError, api } from '../api/client'
import type { Dealer } from '../api/types'
import { Modal } from '../components/AppModal'
import { DataGrid } from '../components/DataGrid'
import { EM_DASH } from '../format'
import { confirmAction } from '../components/confirm'

interface PreviewRow {
  selected: boolean
  needs_attention: boolean
  attention_reason: string
  item_name: string
  manufacturer: string
  family: string
  sku: string
  internal_sku: string
  description: string
  component_type: string
  /** Subscription/warranty term in months — null for a term-less row
   * (typical for Hardware/Accessories). DP-11. */
  term_months?: number | null
  /** True when this row's discount is not a catalog constant but depends on
   * a BOM line's chosen term — the Fortinet price list's "depends on the
   * number of years" hardware row (DP-11 / docs/features/FORTINET-TERMS.md).
   * The importer flags this instead of writing a false 0% discount; see the
   * `warnings` list below for which rows it did this to. */
  discount_by_term?: boolean
  /**
   * Set only for a DM row matched against an existing catalog item (see
   * `utils/import_preview.py::from_dm_result`). Informational, never a
   * warning — the row gets its own "already in catalog" badge, and what
   * happens to it is decided by the `duplicates` policy at commit time,
   * the same as every other row.
   */
  existing_item_id?: number | null
  existing_item_name?: string
  /** The source file's own row, kept for provenance. */
  raw_source_row?: Record<string, unknown> | null
  [key: string]: unknown
}

/**
 * The corrections worth remembering: rows the importer flagged, that the user
 * kept, and where the manufacturer they chose differs from what the file said.
 *
 * A row where the user typed the raw value straight back teaches nothing, and
 * the same unmapped name usually repeats hundreds of times in one file — so
 * the pairs are de-duplicated and counted.
 */
function resolvedAliases(rows: PreviewRow[]): { raw: string; to: string; rows: number }[] {
  const counts = new Map<string, { raw: string; to: string; rows: number }>()
  for (const row of rows) {
    if (!row.needs_attention || !row.selected || !row.manufacturer) continue
    const raw = String(row.raw_source_row?.raw_manufacturer ?? '').trim()
    if (!raw) continue
    if (raw.toLowerCase() === row.manufacturer.trim().toLowerCase()) continue
    const to = row.manufacturer.trim()
    // JSON, not a delimiter: a manufacturer name can contain whatever
    // character a dealer file happened to put there, and a colliding
    // key would merge two distinct mappings into one.
    const key = JSON.stringify([raw, to])
    const seen = counts.get(key)
    if (seen) seen.rows += 1
    else counts.set(key, { raw, to, rows: 1 })
  }
  return [...counts.values()]
}

/**
 * How urgently a row deserves to be seen first in the preview grid: an
 * unresolved row and a row already in the catalog are both worth surfacing
 * ahead of the ordinary ones, even though only the former is a "warning" —
 * see the `flag` column, which renders them as two different badges.
 */
function attentionRank(row: PreviewRow): number {
  return (row.needs_attention ? 1 : 0) + (row.existing_item_id ? 1 : 0)
}

interface CommitResult {
  items_saved: number
  prices_saved: number
  added: number
  updated: number
  skipped: number
}

interface UploadResult {
  upload_id: string
  detected_format: string
  rows: PreviewRow[]
  warnings: string[]
  needs_attention: number
  /**
   * Rows matched against an existing catalog item (DM only). Counted apart
   * from `needs_attention`: a matched row needs no fix, only the
   * `duplicates` policy choice that already applies to every row.
   */
  already_in_catalog: number
  /**
   * The file is a DM dealer price list and no dealer was named.
   *
   * Not an error: the header parsed and the format is known. DM tags every
   * price row with a dealer and a whole-file currency, and neither is in the
   * file — so the server asks instead of guessing, and the same file is sent
   * again with the answer.
   */
  needs_dealer: boolean
}

export function CatalogImportModal({
  opened, onClose,
}: { opened: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<UploadResult | null>(null)
  const [rows, setRows] = useState<PreviewRow[]>([])
  const [error, setError] = useState<string | null>(null)
  // What to do with an item already in the catalog. `skip` is the desktop's
  // default and the safe one: someone loading a file to see what is in it
  // should not have prices rewritten underneath them.
  const [duplicates, setDuplicates] = useState('skip')
  // Only used for a DM price list; the server ignores them otherwise.
  const [dealerId, setDealerId] = useState<string | null>(null)
  const [currency, setCurrency] = useState('ILS')
  const [needsDealer, setNeedsDealer] = useState(false)
  // Set only after detection has already failed — see the alert below.
  const [forcedFormat, setForcedFormat] = useState<string | null>(null)
  const [detectionFailed, setDetectionFailed] = useState(false)

  const dealers = useQuery({
    queryKey: ['dealers'],
    queryFn: () => api.get<Dealer[]>('/dealers'),
    enabled: opened,
  })

  const upload = useMutation({
    mutationFn: (f: File) => {
      const params = new URLSearchParams()
      if (dealerId) { params.set('dealer_id', dealerId); params.set('currency', currency) }
      if (forcedFormat) params.set('format', forcedFormat)
      const qs = params.toString()
      return api.upload<UploadResult>(
        `/catalog-import/upload${qs ? `?${qs}` : ''}`, f)
    },
    onSuccess: (data) => {
      if (data.needs_dealer) {
        // Stay on step one and reveal the picker. Nothing was stored, and the
        // user still holds the file, so the same one goes back up with the
        // answer.
        setNeedsDealer(true)
        setError(null)
        return
      }
      setResult(data)
      // A dealer's updated price list is the normal reason to re-import a DM
      // file, so default to "update price" and save re-selecting it on every
      // upload. The standard/Cisco/Fortinet formats keep the safe default —
      // someone loading a file to see what is in it should not have prices
      // rewritten underneath them.
      setDuplicates(data.detected_format === 'dealer-dm' ? 'prices' : 'skip')
      // Flagged rows and already-in-catalog rows first: a 4,000-row preview
      // with the problems scattered through it is a preview nobody reads.
      setRows([...data.rows].sort((a, b) => attentionRank(b) - attentionRank(a)))
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'קריאת הקובץ נכשלה.'
      setError(message)
      // Offer the manual override only once detection has actually failed.
      // Showing a format picker up front invites people to override a
      // detector that was about to be right.
      if (message.includes('לא ניתן לזהות') || message.includes('לא נמצאה שורת כותרת')) {
        setDetectionFailed(true)
      }
    },
  })

  const commit = useMutation({
    mutationFn: () =>
      api.post<CommitResult>('/catalog-import/commit', {
        upload_id: result?.upload_id ?? '',
        rows,
        duplicates,
      }),
    onSuccess: async (res) => {
      void qc.invalidateQueries({ queryKey: ['items'] })
      void qc.invalidateQueries({ queryKey: ['master-data'] })
      notifications.show({
        color: 'green',
        // The three counts, not one total: "נשמרו 40" while 38 were skipped
        // reads as success and is the opposite of what happened.
        message: res.prices_saved
          ? `נשמרו ${res.items_saved} פריטים ו-${res.prices_saved} מחירים`
          : `נוספו ${res.added}, עודכנו ${res.updated}, דולגו ${res.skipped}`,
      })
      // Offered after the save, never before: the import is what matters, and
      // a failure to store an alias must not look like a failed import.
      const aliases = resolvedAliases(rows)
      if (aliases.length) {
        const lines = aliases.slice(0, 5)
          .map((a) => `  • "${a.raw}" ← "${a.to}"  (${a.rows} שורות)`)
        if (aliases.length > 5) lines.push(`  ועוד ${aliases.length - 5}…`)
        const message = [
          'לשמור את ההתאמות הבאות לייבוא הבא?',
          '',
          ...lines,
          '',
          'ההתאמות ייזכרו בכל ייבוא עתידי.',
        ].join('\n')
        // Offering to remember something, not destroying anything.
        if (await confirmAction({
          title: 'שמירת התאמות יצרנים',
          message,
          confirmLabel: 'שמור התאמות',
          danger: false,
        })) {
          try {
            await api.put('/master-data/aliases/manufacturers',
              Object.fromEntries(aliases.map((a) => [a.raw, a.to])))
            notifications.show({
              color: 'green',
              message: `${aliases.length} התאמות נשמרו`,
            })
          } catch {
            notifications.show({
              color: 'yellow',
              message: 'הייבוא הצליח, אך שמירת ההתאמות נכשלה.',
            })
          }
        }
      }
      close()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת הייבוא נכשלה.'),
  })

  function close() {
    // Tell the server to drop the temp file rather than waiting for the daily
    // sweep — an abandoned preview otherwise sits on the volume.
    if (result?.upload_id) void api.delete(`/catalog-import/${result.upload_id}`).catch(() => {})
    setFile(null); setResult(null); setRows([]); setError(null)
    setNeedsDealer(false); setDealerId(null); setCurrency('ILS')
    setForcedFormat(null); setDetectionFailed(false)
    onClose()
  }

  function patch(index: number, change: Partial<PreviewRow>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...change } : r)))
  }

  const selectedCount = rows.filter((r) => r.selected).length
  const unresolved = rows.filter((r) => r.selected && r.needs_attention && !r.manufacturer).length

  const columns = useMemo<ColumnDef<PreviewRow, any>[]>(
    () => [
      {
        id: 'sel',
        header: '',
        cell: (c) => (
          <Checkbox
            checked={c.row.original.selected}
            onChange={(e) =>
              patch(rows.indexOf(c.row.original), { selected: e.currentTarget.checked })
            }
            aria-label="כלול שורה"
            size="md"
          />
        ),
      },
      {
        id: 'flag',
        header: '',
        cell: (c) => {
          const row = c.row.original
          // Already in the catalog is informational, not a warning — its
          // own badge, distinct from (and checked before) the yellow one.
          if (row.existing_item_id) {
            return (
              <Badge size="xs" color="blue" variant="light" title={row.existing_item_name}>
                קיים בקטלוג
              </Badge>
            )
          }
          return row.needs_attention ? (
            <Badge size="xs" color="yellow" variant="light" title={row.attention_reason}>
              ⚠
            </Badge>
          ) : null
        },
      },
      { accessorKey: 'item_name', header: 'שם פריט', meta: { code: true } },
      { accessorKey: 'sku', header: 'מק"ט', meta: { code: true } },
      {
        accessorKey: 'internal_sku',
        header: 'מק"ט ארגוני',
        cell: (c) => (
          // A dealer quote never carries the organization's own part number
          // (confirmed on real Cisco quotes: none of them do) — blank is the
          // normal starting state here, filled in now or left for later.
          <TextInput
            size="xs"
            w={110}
            placeholder="למלא מאוחר יותר"
            value={c.row.original.internal_sku ?? ''}
            onChange={(e) =>
              patch(rows.indexOf(c.row.original), { internal_sku: e.currentTarget.value })
            }
          />
        ),
      },
      { accessorKey: 'description', header: 'תיאור', cell: (c) => c.getValue<string>() || EM_DASH },
      {
        accessorKey: 'manufacturer',
        header: 'יצרן',
        cell: (c) => (
          // Editable inline: an unresolved manufacturer is the single most
          // common reason a row is flagged, and fixing it here is the whole
          // purpose of the preview.
          <TextInput
            size="xs"
            w={130}
            value={c.row.original.manufacturer}
            error={c.row.original.needs_attention && !c.row.original.manufacturer}
            onChange={(e) =>
              patch(rows.indexOf(c.row.original), { manufacturer: e.currentTarget.value })
            }
          />
        ),
      },
      { accessorKey: 'family', header: 'משפחה', cell: (c) => c.getValue<string>() || EM_DASH },
      { accessorKey: 'component_type', header: 'סוג', cell: (c) => c.getValue<string>() || EM_DASH },
      {
        accessorKey: 'term_months',
        header: 'תקופה',
        cell: (c) => {
          const months = c.getValue<number | null | undefined>()
          return months ? `${months} חודשים` : EM_DASH
        },
      },
      {
        id: 'discount_by_term',
        header: 'הנחה לפי תקופה',
        cell: (c) =>
          c.row.original.discount_by_term ? (
            <Tooltip label='ההנחה תיגזר לפי התקופה שתיבחר ב-BOM, ולא קבועה כאן (ראה "הערות מהייבוא")'>
              <Badge size="xs" color="blue" variant="light">לפי תקופה</Badge>
            </Tooltip>
          ) : null,
      },
    ],
    [rows],
  )

  return (
    <Modal opened={opened} onClose={close} title="ייבוא קטלוג" size="90%">
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}

        {!result && (
          <>
            {/* The template belongs here, not only on the catalog screen: this
                is the moment someone discovers they need one. */}
            <Alert color="blue" variant="light" title="אין לך קובץ?">
              <Group justify="space-between" align="center" wrap="nowrap">
                <Text size="sm">
                  הורד את התבנית הסטנדרטית, מלא אותה וחזור לכאן. שורה אחת לכל
                  רכיב; עמודות הפריט חוזרות בכל רכיביו.
                  <br />
                  <b>התבנית מגיעה עם שורות דוגמה — מחק אותן</b>, אחרת הן ייובאו
                  יחד עם הנתונים שלך.
                </Text>
                <Button
                  variant="light"
                  style={{ flexShrink: 0 }}
                  onClick={() => void api.download(
                    '/items/import-template.xlsx', 'catalog_import_template.xlsx')}
                >
                  הורד תבנית
                </Button>
              </Group>
            </Alert>
            <FileInput
              label="קובץ קטלוג / הצעת מחיר"
              description="Excel או CSV — Cisco, Fortinet, תבנית סטנדרטית או קובץ דילר"
              placeholder="בחר קובץ…"
              accept=".xlsx,.xls,.xlsm,.csv"
              value={file}
              onChange={(f) => { setFile(f); setError(null) }}
            />
            {detectionFailed && (
              <Alert color="orange" variant="light" title="הפורמט לא זוהה">
                <Stack gap="xs">
                  <Text size="sm">
                    אם ידוע לך מהו הפורמט, בחר אותו והקובץ ייקרא לפיו. הבחירה
                    אינה עוקפת בדיקות — הקורא עדיין צריך למצוא את שורת הכותרת
                    שלו, ויסרב אם אינה שם.
                  </Text>
                  <Select
                    label="קרא את הקובץ כ..."
                    w={280}
                    clearable
                    clearButtonProps={{ "aria-label": "נקה", size: "md" }}
                    placeholder="זיהוי אוטומטי"
                    data={[
                      { value: 'standard', label: 'תבנית סטנדרטית של המערכת' },
                      { value: 'cisco', label: 'BOM של Cisco' },
                      { value: 'fortinet', label: 'BOM של Fortinet' },
                    ]}
                    value={forcedFormat}
                    onChange={setForcedFormat}
                  />
                </Stack>
              </Alert>
            )}
            {needsDealer && (
              <Alert color="yellow" variant="light" title="מחירון ספק (DM)">
                <Stack gap="xs">
                  <Text size="sm">
                    הקובץ זוהה כמחירון ספק בפורמט DM. הפורמט אינו כולל את שם
                    הספק ואת המטבע, ושניהם מתייגים כל שורת מחיר — לכן יש לבחור
                    אותם לפני הקריאה.
                  </Text>
                  <Group>
                    <Select
                      label="ספק"
                      required
                      searchable
                      w={220}
                      placeholder="בחר ספק"
                      data={(dealers.data ?? []).map((d) => ({
                        value: String(d.id), label: d.name,
                      }))}
                      value={dealerId}
                      onChange={(v) => {
                        setDealerId(v)
                        // The dealer's own default is the best first guess at
                        // the file's currency.
                        const d = dealers.data?.find((x) => String(x.id) === v)
                        if (d?.default_currency) setCurrency(d.default_currency)
                      }}
                    />
                    <Select
                      label="מטבע הקובץ"
                      w={140}
                      data={['ILS', 'USD']}
                      value={currency}
                      onChange={(v) => setCurrency(v ?? 'ILS')}
                    />
                  </Group>
                </Stack>
              </Alert>
            )}
            {file && (
              <Button
                onClick={() => upload.mutate(file)}
                loading={upload.isPending}
                disabled={needsDealer && !dealerId}
              >
                קרא קובץ
              </Button>
            )}
          </>
        )}

        {result && (
          <>
            <Group justify="space-between">
              <Group gap="xs">
                <Text size="sm">פורמט שזוהה:</Text>
                <Badge variant="light">{result.detected_format}</Badge>
                <Text size="sm" c="dimmed">
                  {rows.length.toLocaleString('he-IL')} שורות · {selectedCount.toLocaleString('he-IL')} נבחרו
                </Text>
              </Group>
              <Group gap="xs">
                <Button size="xs" variant="subtle" onClick={() => setRows((r) => r.map((x) => ({ ...x, selected: true })))}>
                  בחר הכל
                </Button>
                <Button size="xs" variant="subtle" onClick={() => setRows((r) => r.map((x) => ({ ...x, selected: false })))}>
                  נקה בחירה
                </Button>
              </Group>
            </Group>

            {result.needs_attention > 0 && (
              <Alert color="yellow" variant="light">
                {result.needs_attention} שורות דורשות תשומת לב — לרוב יצרן שלא זוהה.
                שורות כאלה שיישארו ללא יצרן לא יישמרו.
              </Alert>
            )}

            {result.already_in_catalog > 0 && (
              <Alert color="blue" variant="light">
                {result.already_in_catalog} שורות כבר קיימות בקטלוג — הטיפול בהן ייקבע
                לפי "פריט שכבר קיים בקטלוג" שנבחר למטה.
              </Alert>
            )}

            {result.warnings.length > 0 && (
              <Alert color="gray" variant="light" title={`${result.warnings.length} הערות מהייבוא`}>
                <Text size="xs">{result.warnings.slice(0, 6).join(' · ')}</Text>
              </Alert>
            )}

            <Paper withBorder>
              <DataGrid data={rows} columns={columns} maxHeight="50vh" stickyFirstColumn={false} />
            </Paper>

            <Group justify="space-between" align="flex-end">
              <Select
                label="פריט שכבר קיים בקטלוג"
                description="קובץ ספק: לפי מק״ט ויצרן · קטלוג רגיל: לפי שם הפריט"
                w={260}
                data={[
                  { value: 'skip', label: 'דלג — שמור את הקיים' },
                  { value: 'prices', label: 'עדכן מחיר, הנחה ותקופה' },
                  { value: 'replace', label: 'החלף את הפריט כולו' },
                ]}
                value={duplicates}
                onChange={(v) => setDuplicates(v ?? 'skip')}
              />
              <Group>
              <Button variant="default" onClick={close}>ביטול</Button>
              <Button
                onClick={() => commit.mutate()}
                loading={commit.isPending}
                disabled={!selectedCount}
              >
                שמור {selectedCount.toLocaleString('he-IL')} שורות
                {unresolved > 0 && ` (${unresolved} יידלגו)`}
              </Button>
              </Group>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  )
}
